import React from 'react';
import { useForm } from '../../context/FormContext';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { detectEmailNameMismatch } from '../../lib/emailNameMismatch';
import { detectEmailTypo } from '../../lib/emailTypo';
import { titleCaseName } from '../../lib/nameCase';

const TITLES = ['Mr', 'Mrs', 'Miss', 'Ms'];

const TITLE_TO_GENDER = { Mr: 'Male', Mrs: 'Female', Miss: 'Female', Ms: 'Female' };

const normalizeUKMobile = (raw) => {
  let phone = raw.replace(/[\s\-()]/g, '');
  if (phone.startsWith('+44')) phone = '0' + phone.slice(3);
  else if (phone.startsWith('0044')) phone = '0' + phone.slice(4);
  else if (phone.startsWith('44') && phone.length >= 12) phone = '0' + phone.slice(2);
  return phone;
};

const isValidUKMobile = (phone) => {
  const normalized = normalizeUKMobile(phone);
  return /^07\d{9}$/.test(normalized);
};

const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
};

export const PersonalAndContactStep = () => {
  const { formData, updateFormData } = useForm();

  const handleTitleChange = (title) => {
    const updates = { title };
    // Auto-set gender from title if gender not manually overridden or is empty
    if (!formData.gender || TITLE_TO_GENDER[formData.title] === formData.gender) {
      updates.gender = TITLE_TO_GENDER[title] || '';
    }
    updateFormData(updates);
  };

  const handleMobileChange = (e) => {
    const raw = e.target.value;
    const normalized = normalizeUKMobile(raw);
    // If user typed international format, store the normalized version
    if (raw !== normalized && (raw.startsWith('+') || raw.startsWith('00'))) {
      updateFormData({ mobile_number: normalized });
    } else {
      updateFormData({ mobile_number: raw });
    }
  };

  const mobileValue = formData.mobile_number || '';
  const mobileHasInput = mobileValue.length > 0;
  const mobileValid = mobileHasInput && isValidUKMobile(mobileValue);
  const mobileInvalid = mobileHasInput && mobileValue.length >= 6 && !mobileValid;

  const emailValue = formData.email_address || '';
  const emailHasInput = emailValue.length > 0;
  const emailValid = emailHasInput && isValidEmail(emailValue);
  const emailInvalid = emailHasInput && emailValue.includes('@') && !emailValid;
  // Soft mismatch warning: only computed once the email is structurally valid
  // (avoids flashing the warning as the user types). Returns null when nothing
  // looks off — shared/business prefixes and applicant-name matches stay silent.
  const emailMismatch = emailValid
    ? detectEmailNameMismatch({
        email: emailValue,
        firstName: formData.first_name,
        surname: formData.surname,
      })
    : null;
  // Domain typo detection (e.g. gmial.com → gmail.com). Runs as soon as a domain
  // is typed even if structurally invalid — typos are often the reason the address
  // doesn't validate yet.
  const emailTypo = emailHasInput && emailValue.includes('@')
    ? detectEmailTypo(emailValue)
    : null;

  return (
    <div className="space-y-6" data-testid="personal-contact-step">
      <div className="text-center">
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Your Details
        </h2>
        <p className="text-slate-600 hidden md:block">Tell us about yourself and how to contact you</p>
      </div>

      <div className="max-w-md mx-auto space-y-4">
        {/* Title */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">
            Title <span className="text-red-500">*</span>
          </Label>
          <div className="flex gap-2">
            {TITLES.map(title => (
              <button
                key={title}
                onClick={() => handleTitleChange(title)}
                className={`flex-1 py-3 rounded-lg border-2 font-medium transition-all ${
                  formData.title === title
                    ? 'brand-border brand-bg-light brand-text'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
                data-testid={`title-${title.toLowerCase()}`}
              >
                {title}
              </button>
            ))}
          </div>
        </div>

        {/* Gender */}
        <div>
          <Label className="text-sm font-medium text-slate-700 mb-2 block">
            Gender <span className="text-red-500">*</span>
          </Label>
          <div className="flex gap-2">
            {['Male', 'Female'].map(g => (
              <button
                key={g}
                onClick={() => updateFormData({ gender: g })}
                className={`flex-1 py-3 rounded-lg border-2 font-medium transition-all ${
                  formData.gender === g
                    ? 'brand-border brand-bg-light brand-text'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
                data-testid={`gender-${g.toLowerCase()}`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>

        {/* First Name */}
        <div>
          <Label htmlFor="first_name" className="text-sm font-medium text-slate-700 mb-2 block">
            First Name <span className="text-red-500">*</span>
          </Label>
          <Input
            id="first_name"
            placeholder="Your first name"
            value={formData.first_name}
            onChange={(e) => updateFormData({ first_name: e.target.value })}
            onBlur={(e) => {
              const cased = titleCaseName(e.target.value);
              if (cased !== e.target.value) updateFormData({ first_name: cased });
            }}
            className="h-12"
            autoComplete="given-name"
            data-testid="first-name-input"
          />
        </div>

        {/* Surname */}
        <div>
          <Label htmlFor="surname" className="text-sm font-medium text-slate-700 mb-2 block">
            Surname <span className="text-red-500">*</span>
          </Label>
          <Input
            id="surname"
            placeholder="Your surname"
            value={formData.surname}
            onChange={(e) => updateFormData({ surname: e.target.value })}
            onBlur={(e) => {
              const cased = titleCaseName(e.target.value);
              if (cased !== e.target.value) updateFormData({ surname: cased });
            }}
            className="h-12"
            autoComplete="family-name"
            data-testid="surname-input"
          />
        </div>

        {/* Mobile Number */}
        <div>
          <Label htmlFor="mobile_number" className="text-sm font-medium text-slate-700 mb-2 block">
            Mobile Number <span className="text-red-500">*</span>
          </Label>
          <Input
            id="mobile_number"
            type="tel"
            placeholder="07XXX XXXXXX"
            value={formData.mobile_number}
            onChange={handleMobileChange}
            className="h-12"
            autoComplete="tel"
            data-testid="mobile-number-input"
          />
          {mobileValid && (
            <p className="flex items-center gap-1 text-xs text-green-600 mt-1" data-testid="mobile-valid">
              <CheckCircle2 className="w-3.5 h-3.5" /> Valid mobile
            </p>
          )}
          {mobileInvalid && (
            <p className="flex items-center gap-1 text-xs text-red-500 mt-1" data-testid="mobile-invalid">
              <XCircle className="w-3.5 h-3.5" /> Must be a valid UK mobile (07...)
            </p>
          )}
        </div>

        {/* Email Address */}
        <div>
          <Label htmlFor="email_address" className="text-sm font-medium text-slate-700 mb-2 block">
            Email Address <span className="text-red-500">*</span>
          </Label>
          <Input
            id="email_address"
            type="email"
            placeholder="your.email@example.com"
            value={formData.email_address}
            onChange={(e) => updateFormData({ email_address: e.target.value })}
            className="h-12"
            autoComplete="email"
            data-testid="email-address-input"
          />
          {emailValid && (
            <p className="flex items-center gap-1 text-xs text-green-600 mt-1" data-testid="email-valid">
              <CheckCircle2 className="w-3.5 h-3.5" /> Valid email
            </p>
          )}
          {emailInvalid && (
            <p className="flex items-center gap-1 text-xs text-red-500 mt-1" data-testid="email-invalid">
              <XCircle className="w-3.5 h-3.5" /> Enter a valid email address
            </p>
          )}
          {emailMismatch && (
            <div
              className="mt-2 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="email-name-mismatch-warning"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
              <span>
                This email contains a different name (<strong>{emailMismatch.detectedName}</strong>).
                Lenders may flag this — the email address must belong to the applicant.
              </span>
            </div>
          )}
          {emailTypo && (
            <div
              className="mt-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              data-testid="email-typo-warning"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 text-amber-600" />
              <span className="flex-1">
                Did you mean <strong>{emailTypo.suggested}</strong>?
              </span>
              <button
                type="button"
                onClick={() => updateFormData({ email_address: emailTypo.suggested })}
                className="text-amber-900 underline font-medium hover:text-amber-700"
                data-testid="email-typo-accept-btn"
              >
                Use this
              </button>
            </div>
          )}
        </div>

        {/* Dependants */}
        <div>
          <Label htmlFor="dependants" className="text-sm font-medium text-slate-700 mb-2 block">
            Number of Dependants <span className="text-red-500">*</span>
          </Label>
          <Select 
            value={String(formData.dependants ?? 0)} 
            onValueChange={(value) => updateFormData({ dependants: parseInt(value) })}
          >
            <SelectTrigger className="h-12" data-testid="dependants-select">
              <SelectValue>{String(formData.dependants ?? 0)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {[...Array(11)].map((_, i) => (
                <SelectItem key={i} value={String(i)}>{i}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default PersonalAndContactStep;
